import { useState } from 'react';
import { Button, Dialog, Notice } from '@ebu/ui';
import { useI18n } from '../../i18n/i18n';
import { useGuest } from './guest-state';

/**
 * Explains optional local saving and offers a visible "Clear my data" action (spec: Core feature 1
 * and Guest persistence). Clearing asks for confirmation in a dialog; the result is announced.
 */
export function LocalDataPanel({
  headingLevel = 2,
  embedded = false,
}: {
  headingLevel?: 2 | 3;
  /** Inside a dialog that already shows the title. */
  embedded?: boolean;
}) {
  const { t, plural, errorMessage } = useI18n();
  const { storage, savedChoices, projectCount, clearLocalData } = useGuest();
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<'cleared' | 'failed' | null>(null);
  const Heading = headingLevel === 2 ? 'h2' : 'h3';

  const confirm = async () => {
    const ok = await clearLocalData();
    setConfirming(false);
    setResult(ok ? 'cleared' : 'failed');
  };

  return (
    <section
      className={embedded ? 'local-data local-data--embedded' : 'panel local-data'}
      aria-label={embedded ? t('data.title') : undefined}
      aria-labelledby={embedded ? undefined : 'local-data-title'}
    >
      {!embedded && <Heading id="local-data-title">{t('data.title')}</Heading>}
      <p>{t('data.body')}</p>
      <p className="local-data__warning">{t('data.warning')}</p>
      {storage === 'unavailable' ? (
        <Notice tone="warning">
          <p>{errorMessage('WEB-STORAGE-001')}</p>
        </Notice>
      ) : (
        <p data-testid="local-data-status">
          {savedChoices
            ? `${t('data.savedChoices')} ${plural('data.projects', projectCount)}.`
            : t('data.nothingSaved')}
        </p>
      )}
      <Button
        variant="secondary"
        icon="trash"
        disabled={storage !== 'ready'}
        onClick={() => {
          setResult(null);
          setConfirming(true);
        }}
      >
        {t('data.clear')}
      </Button>
      <div aria-live="polite">
        {result === 'cleared' && (
          <Notice tone="success">
            <p>{t('data.cleared')}</p>
          </Notice>
        )}
        {result === 'failed' && (
          <Notice tone="error">
            <p>{errorMessage('WEB-STORAGE-001')}</p>
          </Notice>
        )}
      </div>
      <Dialog
        open={confirming}
        title={t('data.clearTitle')}
        description={<p>{t('data.clearBody')}</p>}
        onCancel={() => setConfirming(false)}
        initialFocus="first"
      >
        <div className="button-row">
          <Button variant="secondary" onClick={() => setConfirming(false)}>
            {t('data.cancel')}
          </Button>
          <Button variant="danger" icon="trash" onClick={() => void confirm()}>
            {t('data.clear')}
          </Button>
        </div>
      </Dialog>
    </section>
  );
}

/** Header/welcome button that opens the same panel in a dialog, so it is reachable everywhere. */
export function LocalDataDialogButton({
  variant = 'secondary',
  className,
}: {
  variant?: 'secondary' | 'quiet';
  /** Replaces the button styling (e.g. a text link in the utility bar). */
  className?: string;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <>
      {className ? (
        <button type="button" className={className} onClick={() => setOpen(true)}>
          {t('data.open')}
        </button>
      ) : (
        <Button variant={variant} onClick={() => setOpen(true)}>
          {t('data.open')}
        </Button>
      )}
      <Dialog open={open} title={t('data.title')} onCancel={() => setOpen(false)}>
        <LocalDataPanel embedded />
        <div className="button-row">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            {t('data.close')}
          </Button>
        </div>
      </Dialog>
    </>
  );
}
